import { useState, type ClipboardEvent as ReactClipboardEvent } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, Trash2 } from "lucide-react";

export type ManualProductEntryRow = {
  name: string;
  sku: string;
  price: string;
  costPrice: string;
  stock: string;
  category: string;
  brand: string;
  reorderLevel: string;
  unit: string;
};

export function createManualProductEntryRow(): ManualProductEntryRow {
  return {
    name: "",
    sku: "",
    price: "",
    costPrice: "",
    stock: "",
    category: "",
    brand: "",
    reorderLevel: "",
    unit: "",
  };
}

const columns: {
  key: keyof ManualProductEntryRow;
  label: string;
  width: string;
  numeric?: boolean;
}[] = [
  { key: "name", label: "Product name *", width: "min-w-52" },
  { key: "sku", label: "SKU / barcode", width: "min-w-40" },
  { key: "price", label: "Selling price *", width: "min-w-36", numeric: true },
  { key: "costPrice", label: "Cost price", width: "min-w-32", numeric: true },
  { key: "stock", label: "Opening stock", width: "min-w-32", numeric: true },
  { key: "category", label: "Category", width: "min-w-36" },
  { key: "brand", label: "Brand", width: "min-w-32" },
  { key: "reorderLevel", label: "Reorder level", width: "min-w-32", numeric: true },
  { key: "unit", label: "Unit", width: "min-w-28" },
];

function hasRowData(row: ManualProductEntryRow): boolean {
  return Object.values(row).some(value => value.trim() !== "");
}

function parseNonNegativeNumber(value: string): number | null {
  if (!value.trim()) return null;
  const parsed = Number(value.replace(/[₦$£€,\s]/g, ""));
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : Number.NaN;
}

export function ManualProductEntryGrid({
  rows,
  rowLimit,
  onRowsChange,
  onReview,
}: {
  rows: ManualProductEntryRow[];
  rowLimit: number;
  onRowsChange: (rows: ManualProductEntryRow[]) => void;
  onReview: (rows: Record<string, string>[]) => void;
}) {
  const [error, setError] = useState("");
  const populatedCount = rows.filter(hasRowData).length;

  const handlePaste = (
    event: ReactClipboardEvent<HTMLInputElement>,
    startRow: number,
    startColumn: number,
  ) => {
    const text = event.clipboardData.getData("text/plain");
    if (!text.includes("\t") && !/[\r\n]/.test(text)) return;

    event.preventDefault();
    const matrix = text
      .replace(/\r\n?/g, "\n")
      .split("\n");
    while (matrix.length > 1 && matrix[matrix.length - 1] === "") matrix.pop();
    const cells = matrix.map(line => line.split("\t"));
    const widestRow = Math.max(...cells.map(line => line.length));

    if (startColumn + widestRow > columns.length) {
      setError("The pasted range extends past the last column. Start in an earlier cell or paste fewer columns.");
      return;
    }
    if (startRow + cells.length > rowLimit) {
      setError(`This batch exceeds your ${rowLimit.toLocaleString()}-row product limit.`);
      return;
    }

    const nextRows = rows.map(row => ({ ...row }));
    while (nextRows.length < startRow + cells.length) nextRows.push(createManualProductEntryRow());
    cells.forEach((line, rowOffset) => {
      line.forEach((value, columnOffset) => {
        const column = columns[startColumn + columnOffset];
        if (column) nextRows[startRow + rowOffset][column.key] = value.trim();
      });
    });
    onRowsChange(nextRows);
    setError("");
  };

  const addRow = () => {
    if (rows.length >= rowLimit) return;
    onRowsChange([...rows, createManualProductEntryRow()]);
    setError("");
  };

  const reviewProducts = () => {
    const populatedRows = rows
      .map((row, index) => ({ row, rowNumber: index + 1 }))
      .filter(({ row }) => hasRowData(row));
    if (!populatedRows.length) {
      setError("Enter at least one product before continuing.");
      return;
    }
    if (populatedRows.length > rowLimit) {
      setError(`This batch exceeds your ${rowLimit.toLocaleString()}-row product limit.`);
      return;
    }

    const errors: string[] = [];
    for (const { row, rowNumber } of populatedRows) {
      const prefix = `Row ${rowNumber}`;
      if (!row.name.trim()) errors.push(`${prefix}: product name is required.`);
      const price = parseNonNegativeNumber(row.price);
      if (price === null || Number.isNaN(price) || price <= 0) {
        errors.push(`${prefix}: enter a selling price greater than zero.`);
      }
      for (const [label, value] of [
        ["cost price", row.costPrice],
        ["opening stock", row.stock],
      ]) {
        const parsed = parseNonNegativeNumber(value);
        if (Number.isNaN(parsed)) errors.push(`${prefix}: ${label} must be zero or greater.`);
      }
      const reorderLevel = parseNonNegativeNumber(row.reorderLevel);
      if (Number.isNaN(reorderLevel) || (reorderLevel !== null && !Number.isInteger(reorderLevel))) {
        errors.push(`${prefix}: reorder level must be a whole number of zero or greater.`);
      }
    }

    if (errors.length) {
      setError(errors.slice(0, 5).join(" ") + (errors.length > 5 ? ` And ${errors.length - 5} more issue(s).` : ""));
      return;
    }

    onReview(populatedRows.map(({ row }) => ({
      "Product Name": row.name.trim(),
      "SKU / Barcode": row.sku.trim(),
      "Selling Price": row.price.trim(),
      "Cost Price": row.costPrice.trim(),
      "Stock": row.stock.trim(),
      "Category": row.category.trim(),
      "Brand": row.brand.trim(),
      "Reorder Level": row.reorderLevel.trim(),
      "Unit": row.unit.trim(),
    })));
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-1">
        <h3 className="font-semibold">Enter or paste products</h3>
        <p className="text-sm text-muted-foreground">
          Paste tab-separated rows from Excel or Google Sheets, or type directly. Start at any cell; the column order is shown below.
        </p>
        <p className="text-xs text-muted-foreground">
          One single-SKU product per row. Leave SKU blank to generate one. For size or colour variants, use the CSV/Excel template.
        </p>
      </div>

      <div className="overflow-auto rounded-lg border max-h-[46vh]" data-testid="grid-manual-products">
        <table className="w-full border-collapse text-sm">
          <thead className="sticky top-0 z-10 bg-muted">
            <tr>
              <th className="w-12 px-3 py-2 text-left text-xs font-semibold text-muted-foreground">#</th>
              {columns.map(column => (
                <th key={column.key} className={`px-2 py-2 text-left text-xs font-semibold text-muted-foreground ${column.width}`}>
                  {column.label}
                </th>
              ))}
              <th className="w-12 px-2 py-2" aria-label="Row actions" />
            </tr>
          </thead>
          <tbody className="divide-y">
            {rows.map((row, rowIndex) => (
              <tr key={rowIndex} data-testid={`row-manual-product-${rowIndex + 1}`}>
                <td className="px-3 py-1.5 text-xs tabular-nums text-muted-foreground">{rowIndex + 1}</td>
                {columns.map((column, columnIndex) => (
                  <td key={column.key} className="px-1.5 py-1">
                    <Input
                      value={row[column.key]}
                      inputMode={column.numeric ? "decimal" : "text"}
                      aria-label={`Row ${rowIndex + 1}, ${column.label}`}
                      data-testid={`input-manual-product-${column.key}-${rowIndex + 1}`}
                      className="h-9 border-transparent bg-transparent shadow-none focus-visible:border-input focus-visible:bg-background focus-visible:ring-1"
                      onChange={event => {
                        const nextRows = rows.map((current, index) =>
                          index === rowIndex ? { ...current, [column.key]: event.target.value } : current
                        );
                        onRowsChange(nextRows);
                        setError("");
                      }}
                      onPaste={event => handlePaste(event, rowIndex, columnIndex)}
                    />
                  </td>
                ))}
                <td className="px-1 py-1">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    aria-label={`Remove product row ${rowIndex + 1}`}
                    data-testid={`button-remove-manual-product-${rowIndex + 1}`}
                    disabled={rows.length === 1}
                    onClick={() => {
                      onRowsChange(rows.filter((_, index) => index !== rowIndex));
                      setError("");
                    }}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {error && (
        <div role="alert" data-testid="status-manual-product-error" className="rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive">
          {error}
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-3">
        <Button
          type="button"
          variant="outline"
          size="sm"
          data-testid="button-add-manual-product-row"
          disabled={rows.length >= rowLimit}
          onClick={addRow}
        >
          <Plus className="mr-1.5 h-4 w-4" /> Add row
        </Button>
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-xs text-muted-foreground">
            {populatedCount.toLocaleString()} product{populatedCount === 1 ? "" : "s"} · up to {rowLimit.toLocaleString()} per batch
          </span>
          <Button
            type="button"
            data-testid="button-review-manual-products"
            disabled={populatedCount === 0}
            onClick={reviewProducts}
          >
            Review {populatedCount.toLocaleString()} product{populatedCount === 1 ? "" : "s"}
          </Button>
        </div>
      </div>
    </div>
  );
}
